import {
  Activity,
  Cable,
  CalendarClock,
  type LucideIcon,
  Plug,
  Upload,
  Workflow,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  type SubsidiaryScopedNavItem,
  SubsidiaryScopedTabsLayout,
} from '~/features/platform/components/subsidiary-scoped-tabs-layout';

/**
 * Item in the Integrations tab bar. Add a new entry here (plus its route file
 * under `routes/.../integrations/`) to surface another sub-page. Catalogue and
 * analytics browsing tabs live in the Explorer section instead — see
 * `explorer-layout.tsx`.
 */
interface IntegrationsNavItem {
  to: string;
  /** i18n key under `integrations:layout.nav`. */
  labelKey: string;
  /** Leading icon shown in the tab. */
  icon: LucideIcon;
  /** Only show to super admins (the route is guarded separately). */
  superAdminOnly?: boolean;
}

const navItems: IntegrationsNavItem[] = [
  { to: '/integrations/connections', labelKey: 'connections', icon: Cable, superAdminOnly: true },
  { to: '/integrations/connectors', labelKey: 'connectors', icon: Plug, superAdminOnly: true },
  { to: '/integrations/pipelines', labelKey: 'pipelines', icon: Workflow, superAdminOnly: true },
  {
    to: '/integrations/schedules',
    labelKey: 'schedules',
    icon: CalendarClock,
    superAdminOnly: true,
  },
  { to: '/integrations/health', labelKey: 'health', icon: Activity, superAdminOnly: true },
  {
    to: '/integrations/upload-delivery',
    labelKey: 'uploadDelivery',
    icon: Upload,
    superAdminOnly: true,
  },
];

/**
 * Layout shell for the Integrations section — supplies its header copy and tab
 * config to the shared subsidiary-scoped shell. Mounted by
 * `/integrations/route.tsx`; child pages render into the shell's `<Outlet/>`.
 */
export function IntegrationsSettingsLayout() {
  const { t } = useTranslation('integrations');

  const items: SubsidiaryScopedNavItem[] = navItems.map((item) => ({
    to: item.to,
    label: t(`layout.nav.${item.labelKey}`),
    icon: item.icon,
    ...(item.superAdminOnly ? { superAdminOnly: true } : {}),
  }));

  return (
    <SubsidiaryScopedTabsLayout
      title={t('layout.title')}
      subtitle={t('layout.subtitle')}
      navAriaLabel={t('layout.navAriaLabel')}
      navItems={items}
    />
  );
}
